// Rollout side panel: live vs candidate, the candidate's share of users (the
// A/B split), promote and roll back.

import { useState } from "react";
import {
  errorText,
  promoteCandidate,
  rollbackCandidate,
  setRollout,
} from "../../services/talkTracks";
import type { TalkTrackTopic, TopicStats } from "../../types/talkTrack";

interface Props {
  topic: TalkTrackTopic;
  stats: TopicStats | null;
  // Performance filters are on: the counts would describe a slice, not the arm.
  filtered?: boolean;
  minUsersToPromote: number;
  onDone: (message: string) => Promise<void> | void;
}

export function RolloutPanel({ topic, stats, filtered = false, minUsersToPromote, onDone }: Props) {
  // The page keys this panel by candidate + rollout, so a server-side change
  // re-initialises the slider.
  const [pct, setPct] = useState(topic.rolloutPct);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Users, not conversations: one heavy user can produce dozens of
  // conversations, and the rates weigh each user equally.
  const usersFor = (version: number | null, arm: "control" | "candidate") =>
    stats && !filtered && version !== null
      ? stats.versions
          .filter((v) => v.version === version && v.arm === arm)
          .reduce((n, v) => n + v.users, 0)
      : null;
  const candidateUsers = usersFor(topic.candidateVersion, "candidate");
  const liveUsers = usersFor(topic.liveVersion, "control");

  async function run(label: string, action: () => Promise<unknown>, success: string) {
    setBusy(true);
    setError(null);
    try {
      await action();
      await onDone(success);
    } catch (e) {
      setError(`${label} failed: ${errorText(e)}`);
    } finally {
      setBusy(false);
    }
  }

  function apply() {
    if (pct >= 100 && !confirm(`100% promotes candidate v${topic.candidateVersion} to live. Continue?`)) return;
    run("Rollout", () => setRollout(topic.id, pct),
      pct >= 100 ? `v${topic.candidateVersion} promoted to live ✓` : `Candidate now at ${pct}% ✓`);
  }

  function promote() {
    if (!confirm(`Promote v${topic.candidateVersion} to live for everyone? v${topic.liveVersion ?? "—"} retires.`)) return;
    run("Promote", () => promoteCandidate(topic.id), `v${topic.candidateVersion} promoted to live ✓`);
  }

  function rollback() {
    if (!confirm(`Roll back candidate v${topic.candidateVersion}? Everyone goes back to live v${topic.liveVersion ?? "—"}.`)) return;
    run("Roll back", () => rollbackCandidate(topic.id), `Candidate v${topic.candidateVersion} rolled back`);
  }

  return (
    <div className="tt-side-section">
      <div className="panel-head">Rollout</div>
      <div className="meta-grid">
        <div className="meta-item">
          <span className="meta-label">Live</span>
          <span className="meta-value">
            {topic.liveVersion !== null ? `v${topic.liveVersion}` : "—"}
            {liveUsers !== null && <span className="muted"> · {liveUsers} users</span>}
          </span>
        </div>
        <div className="meta-item">
          <span className="meta-label">Candidate</span>
          <span className="meta-value">
            {topic.candidateVersion !== null ? `v${topic.candidateVersion} at ${topic.rolloutPct}%` : "none"}
            {candidateUsers !== null && <span className="muted"> · {candidateUsers} users</span>}
          </span>
        </div>
      </div>

      {topic.candidateVersion === null ? (
        <div className="muted tt-small" style={{ marginTop: 8 }}>
          No A/B test running. Publish an approved version below 100% to start one.
        </div>
      ) : (
        <>
          <label htmlFor="tt-rollout-range">Candidate share of users</label>
          <div className="tt-inline">
            <input id="tt-rollout-range" type="range" min={0} max={100} step={5} value={pct}
              onChange={(e) => setPct(parseInt(e.target.value, 10))} />
            <input type="number" min={0} max={100} value={pct} aria-label="Candidate rollout percent"
              onChange={(e) => setPct(Math.min(100, Math.max(0, parseInt(e.target.value || "0", 10))))} />
            <span className="muted">%</span>
          </div>
          <div className="tt-actions">
            <button className="btn btn-primary" onClick={apply} disabled={busy || pct === topic.rolloutPct}>Apply</button>
            <button className="btn btn-good" onClick={promote} disabled={busy}>Promote</button>
            <button className="btn btn-danger" onClick={rollback} disabled={busy}>Roll back</button>
          </div>
          {candidateUsers !== null && candidateUsers < minUsersToPromote && (
            <div className="tt-reason">
              The candidate has {candidateUsers} user{candidateUsers === 1 ? "" : "s"} in the last {stats?.days} days —
              wait for at least {minUsersToPromote} before promoting on the numbers.
            </div>
          )}
        </>
      )}
      {filtered && topic.candidateVersion !== null && (
        <div className="muted tt-small" style={{ marginTop: 8 }}>
          User counts are hidden while Performance filters are on.
        </div>
      )}
      {error && <div className="error-text">{error}</div>}
    </div>
  );
}
